import { Button } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import { useStore } from './store/hooks.js';
import { useWiring } from './wiring.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';

// Frozen copy, split only so Callout can match the neighbouring control card (icon + title + body).
const RETRYABLE_TITLE = 'Iris could not add this camera.';
const RETRYABLE_BODY = 'Your settings are still here. Try again.';
const UNCERTAIN_TITLE = 'Iris could not confirm that this camera was saved.';
const UNCERTAIN_BODY =
  'Close this dialog and check the Studio’s source list before trying to add it again.';

// Frozen pending copy. Keep the operator on the dialog while the write is in flight (device named).
const PENDING_TITLE = (deviceName: string): string => `Saving ${deviceName} to Studio…`;
const PENDING_BODY = 'Keep this dialog open until it finishes.';
// Shown only when the candidate carries no name yet, so the message never prints "undefined".
const PENDING_FALLBACK_NAME = 'this camera';

/** Save outcomes remain visible when the operator navigates back to review settings. */
export const FinalSaveStatus = () => {
  const attempt = useStore((state) => state.finalSave);
  const wiring = useWiring();
  if (!attempt) return null;
  // While the write is in flight, name the device and tell the operator to keep the dialog open;
  // Save and the explicit close control are gated; Back remains safe through attempt ownership.
  if (attempt.status === 'pending') {
    const deviceName =
      attempt.snapshot.candidate.name ??
      attempt.snapshot.manufacturer.defaultName ??
      PENDING_FALLBACK_NAME;
    return (
      <div className='px-40'>
        <Callout
          role='status'
          aria-live='polite'
          severity='info'
          title={PENDING_TITLE(deviceName)}
          {...DataSTag(Sel.AddVideoDeviceDiscovery, 'final_save_status', attempt.status)}
        >
          <Callout.P>{PENDING_BODY}</Callout.P>
        </Callout>
      </div>
    );
  }
  // Per B1: a superseded attempt renders nothing — it was abandoned by navigation/retarget, not
  // an outcome the operator must act on; showing "could not confirm saved" there is the dead end.
  // 'saved' is silent because the dialog closes on success.
  if (attempt.status === 'saved' || attempt.status === 'superseded') return null;
  const retryable = attempt.status === 'retryable';
  return (
    <div className='px-40'>
      <Callout
        role='status'
        aria-live='polite'
        // retryable = we never sent it (try again); uncertain = it may already exist (don't retry).
        severity={retryable ? 'error' : 'warning'}
        title={retryable ? RETRYABLE_TITLE : UNCERTAIN_TITLE}
        {...DataSTag(Sel.AddVideoDeviceDiscovery, 'final_save_status', attempt.status)}
      >
        <Callout.P>{retryable ? RETRYABLE_BODY : UNCERTAIN_BODY}</Callout.P>
        {!retryable && (
          <div className='mt-8'>
            <Button
              onClick={wiring.handleClose}
              {...STagNew(Sel.AddVideoDeviceDiscovery, 'final_save_close')}
            >
              Close and check sources
            </Button>
          </div>
        )}
      </Callout>
    </div>
  );
};
